import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  Package, 
  Image as ImageIcon, 
  Video, 
  CreditCard,
  Check,
  TrendingUp,
  Calendar
} from "lucide-react";

const UsageBilling = () => {
  const [stats, setStats] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, subRes, plansRes] = await Promise.all([
        axios.get(`${API}/usage/stats`, { withCredentials: true }),
        axios.get(`${API}/user/subscription`, { withCredentials: true }),
        axios.get(`${API}/plans`, { withCredentials: true })
      ]);
      
      setStats(statsRes.data);
      setSubscription(subRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = (plan) => {
    // In production, this would integrate with Razorpay
    toast.info(`Upgrade to ${plan.name} - Payment integration coming soon!`);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const skuUsagePercent = stats?.sku_limit ? (stats.skus_used / stats.sku_limit) * 100 : 0;

  return (
    <DashboardLayout>
      <div data-testid="usage-billing-page" className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-neutral-900">
            Usage & Billing
          </h1>
          <p className="text-neutral-600 mt-1">
            Monitor your usage and manage your subscription
          </p>
        </div>

        {/* Current Plan */}
        <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm text-indigo-600 font-medium mb-1">Current Plan</p>
                <h2 className="font-display text-3xl font-bold text-neutral-900">
                  {subscription?.plan?.name || "Free Trial"}
                </h2>
                {subscription && (
                  <p className="text-neutral-600 mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Valid until {new Date(subscription.valid_to).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-neutral-900">
                  ₹{subscription?.plan?.price_monthly || 0}
                  <span className="text-lg font-normal text-neutral-500">/mo</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Usage Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center">
                  <Package className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-neutral-900">
                {stats?.skus_used || 0} / {stats?.sku_limit || 0}
              </p>
              <p className="text-sm text-neutral-500 mt-1">SKUs Used</p>
              <Progress value={skuUsagePercent} className="mt-3 h-2" />
              <p className="text-xs text-neutral-400 mt-2">
                {stats?.sku_limit - stats?.skus_used || 0} remaining
              </p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-violet-50 rounded-xl flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-violet-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{stats?.images_generated || 0}</p>
              <p className="text-sm text-neutral-500 mt-1">Images Generated</p>
              <p className="text-xs text-neutral-400 mt-2">
                {subscription?.plan?.images_per_sku || 4} per SKU
              </p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Video className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
              <p className="text-3xl font-bold text-neutral-900">{stats?.videos_generated || 0}</p>
              <p className="text-sm text-neutral-500 mt-1">Videos Generated</p>
              <p className="text-xs text-neutral-400 mt-2">
                {subscription?.plan?.video_per_sku || 0} per SKU
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Available Plans */}
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900 mb-4">
            Available Plans
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => {
              const isCurrentPlan = subscription?.plan?.plan_id === plan.plan_id;
              return (
                <Card 
                  key={plan.plan_id}
                  className={`relative overflow-hidden transition-all ${
                    isCurrentPlan 
                      ? 'border-2 border-indigo-600' 
                      : 'border-neutral-200 hover:border-indigo-200'
                  }`}
                >
                  {isCurrentPlan && (
                    <div className="absolute top-0 right-0 bg-indigo-600 text-white text-xs font-semibold px-3 py-1 rounded-bl-lg">
                      Current Plan
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="font-display text-xl font-semibold text-neutral-900 mb-2">
                      {plan.name}
                    </h3>
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className="text-4xl font-bold text-neutral-900">₹{plan.price_monthly}</span>
                      <span className="text-neutral-500">/month</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {plan.features?.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3 text-neutral-700 text-sm">
                          <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      data-testid={`upgrade-${plan.name.toLowerCase()}-btn`}
                      onClick={() => handleUpgrade(plan)}
                      disabled={isCurrentPlan}
                      className={`w-full rounded-full ${
                        isCurrentPlan 
                          ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed' 
                          : 'bg-indigo-700 hover:bg-indigo-800 text-white'
                      }`}
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Payment Note */}
        <Card className="border-neutral-100 bg-neutral-50">
          <CardContent className="p-6 flex items-start gap-4">
            <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <CreditCard className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900 mb-1">Payment Integration</h3>
              <p className="text-sm text-neutral-600">
                Razorpay payment integration is configured for this platform. 
                Secure payments with UPI, Cards, and Net Banking support for Indian users.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default UsageBilling;
